import React, { useCallback, useRef } from "react";
import DynamicLinesDialog from "../../../components/DynamicLinesDialog";
import {
    fetchDataEngine,
    graphqlQuery,
} from "../../../data/Axios/DynamicService";
import { accountsmanagement } from "../../../config";
import { allAccountSubCategories } from "../../../data/Axios/queries";
import { filterAccountSubCategoriesByUseCase } from "../../../data/finance/accountCategoryRules";

const base_url = accountsmanagement.uri;

const ManagePurchaseLinesDialog = ({ open, onClose, purchaseData, refetchData }) => {

    // Keep a ref to loaded options so we can look up names after a mutation
    const accountTypesRef = useRef([]);
    const accountsRef = useRef([]);

    // ── Options loaders ──────────────────────────────────────────────────────────

    const loadAccountTypes = useCallback(async () => {
        const data = await graphqlQuery(allAccountSubCategories, base_url);
        const filtered = filterAccountSubCategoriesByUseCase(
            data || [],
            "PURCHASE_INVOICE_LINE"
        );
        accountTypesRef.current = filtered; // cache for name lookup
        return filtered;
    }, []);

    const loadAccounts = useCallback(async (accountTypeId) => {
        if (!accountTypeId) return [];
        const accs = await fetchDataEngine(
            "allAccountsByColumnValues",
            "accounts",
            { accountSubcategoryId: Number(accountTypeId) },
            ["id", "accountName"],
            base_url
        );
        const result = accs || [];
        accountsRef.current = result; // cache for name lookup
        return result;
    }, []);

    // ── After a create/update mutation, the API returns IDs but not names.     ──
    // This callback receives the raw saved row and enriches it with display     ──
    // names from our cached option lists so the table renders correctly.        ──
    const transformSavedRow = useCallback((savedRow) => {
        const accountType = accountTypesRef.current.find(
            (t) => t.id === savedRow.accountTypeId || t.id === Number(savedRow.accountTypeId)
        );
        const account = accountsRef.current.find(
            (a) => a.id === savedRow.accountId || a.id === Number(savedRow.accountId)
        );

        return {
            ...savedRow,
            accountTypeName:
                savedRow.accountTypeName ||
                accountType?.accountSubCategoryName ||
                accountType?.accountCategoryName ||
                "",
            accountName:
                savedRow.accountName ||
                account?.accountName ||
                "",
        };
    }, []);

    // ── Pre-load dependent options when a row is opened for editing ──────────────

    const handleEditRow = useCallback(
        async (row, setDependentOptions) => {
            if (row?.accountTypeId) {
                const accounts = await loadAccounts(row.accountTypeId);
                setDependentOptions("accountId", accounts);
            }
        },
        [loadAccounts]
    );

    // ── Form fields ──────────────────────────────────────────────────────────────

    const formFields = [
        {
            name: "accountTypeId",
            label: "Account Type",
            type: "select",
        },
        {
            name: "accountId",
            label: "Account",
            type: "dependent-select",
            dependsOn: "accountTypeId",
        },
        {
            name: "description",
            label: "Description",
            type: "text",
        },
        {
            name: "amount",
            label: "Amount",
            type: "number",
        },
    ];

    // ── Table columns ─────────────────────────────────────────────────────────────

    const columns = [
        { field: "id", headerName: "ID", flex: 0.4 },
        {
            field: "accountTypeName",
            headerName: "Account Type",
            flex: 1,
            valueGetter: (params) =>
                params.row.accountTypeName ||
                params.row.accountSubCategoryName ||
                "",
        },
        {
            field: "accountName",
            headerName: "Account Name",
            flex: 1,
            valueGetter: (params) => params.row.accountName || "",
        },
        { field: "description", headerName: "Description", flex: 2 },
        { field: "amount", headerName: "Amount", flex: 1, format: "currency" },
    ];

    // ── Query config ──────────────────────────────────────────────────────────────

    const queryConfig = {
        name: "purchaseInvoiceLinesView",
        modelName: "purchaseInvoiceLines",
        inputField: "purchaseInvoiceId",
        fields: [
            "id",
            "purchaseInvoiceId",
            "accountTypeId",
            "accountTypeName",
            "accountId",
            "accountName",
            "description",
            "amount",
            "isActive",
        ],
    };

    // ── Mutation config ───────────────────────────────────────────────────────────

    const mutationConfig = {
        create: "createPurchaseInvoiceLine",
        update: "updatePurchaseInvoiceLine",
        modelName: "newPurchaseInvoiceLine",
        updateModelName: "updatePurchaseInvoiceLine",
    };

    // ── Options loaders map ───────────────────────────────────────────────────────

    const optionsLoaders = {
        accountTypeId: loadAccountTypes,
        accountId: loadAccounts,
    };

    return (
        <DynamicLinesDialog
            open={open}
            onClose={onClose}
            title="Manage Purchase Lines"
            parentData={purchaseData}
            parentIdField="purchaseInvoiceId"
            baseUrl={base_url}
            queryConfig={queryConfig}
            mutationConfig={mutationConfig}
            refetchData={refetchData}
            formFields={formFields}
            columns={columns}
            optionsLoaders={optionsLoaders}
            additionalMutationData={{ isActive: 1 }}
            onEditRow={handleEditRow}
            // KEY FIX: enrich the row returned by create/update with display names
            transformSavedRow={transformSavedRow}
        />
    );
};

export default ManagePurchaseLinesDialog;
